import type { MessageKey } from "@expertloop/i18n";
import { useState, useEffect } from "react";
import { adminApi } from "../services/api";
import { useI18n } from "@expertloop/i18n/react";
import type { AuditLogEntry } from "@expertloop/shared";

interface AuditResponse {
	entries: AuditLogEntry[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

const ACTION_LABEL_KEYS: Record<string, MessageKey> = {
	admin_login: "adminAudit.action.login",
	admin_logout: "adminAudit.action.logout",
	view_mediation_list: "adminAudit.action.viewMediations",
	view_mediation_detail: "adminAudit.action.viewMediationDetail",
	resolve_mediation: "adminAudit.action.resolveMediation",
	reopen_mediation: "adminAudit.action.reopenMediation",
	generate_mediation_report: "adminAudit.action.generateMediationReport",
	draft_mediation_reply: "adminAudit.action.draftMediationReply",
	view_chat_file: "adminAudit.action.viewChatFile",
	view_chat_video: "adminAudit.action.viewChatVideo",
	view_chat_list: "adminAudit.action.viewChats",
	view_chat_detail: "adminAudit.action.viewChatDetail",
	view_user_list: "adminAudit.action.viewUsers",
	view_user_detail: "adminAudit.action.viewUserDetail",
	view_user_chats: "adminAudit.action.viewUserChats",
	export_data: "adminAudit.action.exportData",
	impersonate_user: "adminAudit.action.impersonateUser",
	impersonated_action: "adminAudit.action.impersonatedAction",
	grant_expert_status: "adminAudit.action.grantExpertStatus",
	revoke_expert_status: "adminAudit.action.revokeExpertStatus",
	view_platform_finance: "adminAudit.action.viewPlatformFinance",
	view_finance_transactions: "adminAudit.action.viewFinanceTransactions",
	admin_password_reset_requested: "adminAudit.action.passwordResetRequested",
	admin_password_reset_completed: "adminAudit.action.passwordResetCompleted",
	admin_password_reset_sent: "adminAudit.action.passwordResetSent",
	admin_created: "adminAudit.action.adminCreated",
	admin_updated: "adminAudit.action.adminUpdated",
	send_user_password_reset: "adminAudit.action.sendUserPasswordReset",
	send_user_verification_email: "adminAudit.action.sendUserVerificationEmail",
};

export function AuditLogPage() {
	const { t } = useI18n();
	const [entries, setEntries] = useState<AuditLogEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [total, setTotal] = useState(0);

	// Filters
	const [actionFilter, setActionFilter] = useState("");
	const [resourceTypeFilter, setResourceTypeFilter] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");

	useEffect(() => {
		const fetchAuditLog = async () => {
			setLoading(true);
			try {
				const params = new URLSearchParams({
					page: page.toString(),
					pageSize: "50",
				});
				if (actionFilter) params.append("action", actionFilter);
				if (resourceTypeFilter) params.append("resourceType", resourceTypeFilter);
				if (startDate) params.append("startDate", new Date(startDate).toISOString());
				if (endDate) params.append("endDate", new Date(endDate).toISOString());

				const data = await adminApi.get<AuditResponse>(`/admin/audit?${params}`);
				setEntries(data.entries);
				setTotalPages(data.totalPages);
				setTotal(data.total);
			} catch (err) {
				setError(err instanceof Error ? err.message : t("adminAudit.loadFailed"));
			} finally {
				setLoading(false);
			}
		};

		fetchAuditLog();
	}, [page, actionFilter, resourceTypeFilter, startDate, endDate]);

	const formatDate = (date: Date | string) => {
		return new Date(date).toLocaleString();
	};

	const getActionBadgeClass = (action: string) => {
		if (action.includes("login") || action.includes("logout")) return "pending";
		if (action.includes("impersonate")) return "mediation";
		if (action.includes("detail")) return "accepted";
		return "completed";
	};

	const getMethodColor = (method: string) => {
		switch (method) {
			case "POST": return "#22c55e"; // green
			case "PUT": return "#f59e0b"; // amber
			case "PATCH": return "#3b82f6"; // blue
			case "DELETE": return "#ef4444"; // red
			default: return "#6b7280"; // gray
		}
	};

	const formatImpersonatedAction = (entry: AuditLogEntry) => {
		const meta = entry.metadata as {
			method?: string;
			path?: string;
			impersonatedUserEmail?: string;
		} | undefined;
		if (!meta) return null;
		return (
			<>
				<span style={{ color: getMethodColor(meta.method || ""), fontWeight: 600 }}>
					{meta.method || "?"}
				</span>
				{" "}{meta.path || "?"}
			</>
		);
	};

	const clearFilters = () => {
		setActionFilter("");
		setResourceTypeFilter("");
		setStartDate("");
		setEndDate("");
		setPage(1);
	};

	return (
		<div>
			<div className="page-header">
				<h1>{t('adminNav.auditLog')}</h1>
				<p>{t('adminAudit.subtitle', { count: total })}</p>
			</div>

			{/* Filters */}
			<div className="card" style={{ marginBottom: "1.5rem" }}>
				<div className="card-body">
					<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", alignItems: "end" }}>
						<div className="form-group" style={{ marginBottom: 0 }}>
							<label>{t('adminAudit.actionLabel')}</label>
							<select
								className="filter-select"
								style={{ width: "100%" }}
								value={actionFilter}
								onChange={(e) => {
									setActionFilter(e.target.value);
									setPage(1);
								}}
							>
								<option value="">{t('adminAudit.allActions')}</option>
								<option value="admin_login">{t('adminAudit.action.login')}</option>
								<option value="admin_logout">{t('adminAudit.action.logout')}</option>
								<option value="view_mediation_list">{t('adminAudit.action.viewMediations')}</option>
								<option value="view_mediation_detail">{t('adminAudit.action.viewMediationDetail')}</option>
								<option value="resolve_mediation">{t('adminAudit.action.resolveMediation')}</option>
								<option value="reopen_mediation">{t('adminAudit.action.reopenMediation')}</option>
								<option value="generate_mediation_report">{t('adminAudit.action.generateMediationReport')}</option>
								<option value="draft_mediation_reply">{t('adminAudit.action.draftMediationReply')}</option>
								<option value="view_chat_file">{t('adminAudit.action.viewChatFile')}</option>
								<option value="view_chat_video">{t('adminAudit.action.viewChatVideo')}</option>
								<option value="view_chat_list">{t('adminAudit.action.viewChats')}</option>
								<option value="view_chat_detail">{t('adminAudit.action.viewChatDetail')}</option>
								<option value="view_user_list">{t('adminAudit.action.viewUsers')}</option>
								<option value="view_user_detail">{t('adminAudit.action.viewUserDetail')}</option>
								<option value="view_user_chats">{t('adminAudit.action.viewUserChats')}</option>
								<option value="impersonate_user">{t('adminAudit.action.impersonateUser')}</option>
								<option value="impersonated_action">{t('adminAudit.impersonatedActionsPlural')}</option>
								<option value="grant_expert_status">{t('adminAudit.action.grantExpertStatus')}</option>
								<option value="revoke_expert_status">{t('adminAudit.action.revokeExpertStatus')}</option>
								<option value="send_user_password_reset">{t('adminAudit.action.sendUserPasswordReset')}</option>
								<option value="send_user_verification_email">{t('adminAudit.action.sendUserVerificationEmail')}</option>
								<option value="admin_created">{t('adminAudit.action.adminCreated')}</option>
								<option value="admin_updated">{t('adminAudit.action.adminUpdated')}</option>
								<option value="admin_password_reset_requested">{t('adminAudit.action.passwordResetRequested')}</option>
								<option value="admin_password_reset_completed">{t('adminAudit.action.passwordResetCompleted')}</option>
								<option value="admin_password_reset_sent">{t('adminAudit.action.passwordResetSent')}</option>
							</select>
						</div>

						<div className="form-group" style={{ marginBottom: 0 }}>
							<label>{t('adminAudit.resourceType')}</label>
							<select
								className="filter-select"
								style={{ width: "100%" }}
								value={resourceTypeFilter}
								onChange={(e) => {
									setResourceTypeFilter(e.target.value);
									setPage(1);
								}}
							>
								<option value="">{t('adminAudit.allTypes')}</option>
								<option value="user">{t('adminUsersPage.user')}</option>
								<option value="chat">{t('adminAudit.chat')}</option>
								<option value="order">{t('adminAudit.orderMediation')}</option>
								<option value="admin">{t('adminAudit.adminResource')}</option>
							</select>
						</div>

						<div className="form-group" style={{ marginBottom: 0 }}>
							<label>{t('adminAudit.startDate')}</label>
							<input
								type="date"
								className="search-input"
								value={startDate}
								onChange={(e) => {
									setStartDate(e.target.value);
									setPage(1);
								}}
							/>
						</div>

						<div className="form-group" style={{ marginBottom: 0 }}>
							<label>{t('adminAudit.endDate')}</label>
							<input
								type="date"
								className="search-input"
								value={endDate}
								onChange={(e) => {
									setEndDate(e.target.value);
									setPage(1);
								}}
							/>
						</div>

						<button className="btn btn-outline btn-sm" onClick={clearFilters}>
							{t('adminAudit.clearFilters')}
						</button>
					</div>
				</div>
			</div>

			{error && <div className="error-message">{error}</div>}

			<div className="card">
				{loading && entries.length === 0 ? (
					<div className="loading">{t('adminAudit.loading')}</div>
				) : entries.length === 0 ? (
					<div className="empty-state">
						<h3>{t('adminAudit.emptyTitle')}</h3>
						<p>{t('adminAudit.emptyBody')}</p>
					</div>
				) : (
					<div className="table-container">
						<table>
							<thead>
								<tr>
									<th>{t('adminAudit.timestamp')}</th>
									<th>{t('adminAudit.admin')}</th>
									<th>{t('adminAudit.actionLabel')}</th>
									<th>{t('adminAudit.resource')}</th>
									<th>{t('adminAudit.ipAddress')}</th>
								</tr>
							</thead>
							<tbody>
								{entries.map((entry) => (
									<tr key={entry.id}>
										<td style={{ whiteSpace: "nowrap" }}>
											{formatDate(entry.createdAt)}
										</td>
										<td>
											<div style={{ fontWeight: 500 }}>{entry.adminEmail}</div>
											<div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
												{t('adminAudit.idPrefix')} {entry.adminId.slice(-8)}
											</div>
										</td>
										<td>
											<span className={`status-badge ${getActionBadgeClass(entry.action)}`}>
												{ACTION_LABEL_KEYS[entry.action] ? t(ACTION_LABEL_KEYS[entry.action]!) : entry.action}
											</span>
											{entry.action === "impersonated_action" && formatImpersonatedAction(entry) && (
												<div style={{ fontSize: "0.75rem", marginTop: "0.25rem", fontFamily: "monospace" }}>
													{formatImpersonatedAction(entry)}
												</div>
											)}
											{entry.action === "impersonate_user" && entry.metadata && (
												<div style={{ fontSize: "0.75rem", color: "var(--color-gray-600)", marginTop: "0.25rem" }}>
													{t('adminAudit.asUser', { email: (entry.metadata as { userEmail?: string }).userEmail ?? '' })}
												</div>
											)}
										</td>
										<td>
											{entry.resourceType ? (
												<div>
													<span style={{ textTransform: "capitalize" }}>
														{entry.resourceType}
													</span>
													{entry.resourceId && (
														<div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
															{entry.resourceId.slice(-12)}
														</div>
													)}
												</div>
											) : (
												<span style={{ color: "var(--color-gray-400)" }}>-</span>
											)}
										</td>
										<td>
											<span style={{ fontSize: "0.875rem", fontFamily: "monospace" }}>
												{entry.ipAddress || "-"}
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{totalPages > 1 && (
				<div className="pagination">
					<button
						className="pagination-btn"
						onClick={() => setPage((p) => Math.max(1, p - 1))}
						disabled={page === 1}
					>
						{t('common.previous')}
					</button>
					<span className="pagination-info">
						{t('common.pageOf', { page, totalPages })}
					</span>
					<button
						className="pagination-btn"
						onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
						disabled={page === totalPages}
					>
						{t('common.next')}
					</button>
				</div>
			)}
		</div>
	);
}
